import type firebase from "firebase/compat/app";
import "firebase/compat/firestore";
import { isRecord, isTimestamp } from "../types/data";
import type { ActivityRecord } from "../types/activity";

const DEFAULT_QUERY_CHUNK_SIZE = 10;
const DEFAULT_QUERY_LIMIT = 40;
const DEFAULT_QUERY_CONCURRENCY = 3;

const chunk = <T>(items: T[], size: number): T[][] => {
  const result: T[][] = [];
  for (let index = 0; index < items.length; index += size) {
    result.push(items.slice(index, index + size));
  }
  return result;
};

const mapWithConcurrency = async <T, R>(items: T[], concurrency: number, mapper: (item: T, index: number) => Promise<R>): Promise<R[]> => {
  if (!items.length) return [];

  const result: R[] = new Array<R>(items.length);
  let nextIndex = 0;
  const worker = async () => {
    while (nextIndex < items.length) {
      const index = nextIndex;
      nextIndex += 1;
      result[index] = await mapper(items[index], index);
    }
  };

  await Promise.all(
    Array.from(
      { length: Math.min(Math.max(1, concurrency), items.length) },
      () => worker()
    )
  );
  return result;
};

const toMillis = (value: unknown) => {
  if (!value) return 0;
  if (typeof value === "number") return value;
  if (isRecord(value) && typeof value.toMillis === "function") return Number(value.toMillis());
  if (isRecord(value) && Number.isFinite(Number(value.seconds))) return Number(value.seconds) * 1000;
  const parsed = isTimestamp(value) ? value.toDate() : value instanceof Date || typeof value === "string" ? new Date(value) : null;
  const time = parsed?.getTime?.();
  return time !== undefined && Number.isFinite(time) ? time : 0;
};

/**
 * Reads recent friend activity with a bounded number of collection-group
 * queries. The previous implementation fetched several documents separately
 * for every friend on every poll, so its read cost grew as friends x events.
 *
 * New activity documents contain actorId and can therefore be queried in
 * groups. Old documents are intentionally not backfilled: notifications are
 * ephemeral and a backfill would recreate the read spike this replaces.
 */
export const loadRecentFriendActivities = async ({
  firestore,
  friendIds,
  since,
  chunkSize = DEFAULT_QUERY_CHUNK_SIZE,
  limit = DEFAULT_QUERY_LIMIT,
  concurrency = DEFAULT_QUERY_CONCURRENCY
}: { firestore: firebase.firestore.Firestore | null | undefined; friendIds?: string[]; since?: number; chunkSize?: number; limit?: number; concurrency?: number }) => {
  if (!firestore?.collectionGroup) return [];

  const normalizedIds = [...new Set(
    (friendIds || []).map((value) => String(value || "").trim()).filter(Boolean)
  )];
  if (!normalizedIds.length) return [];

  const sinceDate = new Date(Number(since) || Date.now());
  const snapshots = await mapWithConcurrency(
    chunk(normalizedIds, Math.max(1, chunkSize)),
    concurrency,
    (actorIds) => firestore
      .collectionGroup("activities")
      .where("actorId", "in", actorIds)
      .where("timestamp", ">", sinceDate)
      .orderBy("timestamp", "desc")
      .limit(limit)
      .get()
  );

  const byKey = new Map<string, { id: string; actorId: string; data: ActivityRecord & { actorId?: string } }>();
  snapshots.forEach((snapshot) => {
    (snapshot?.docs || []).forEach((activityDoc) => {
      const data: ActivityRecord & { actorId?: string } = activityDoc.data?.() || {};
      const actorId = String(data.actorId || "");
      if (!actorId || !normalizedIds.includes(actorId)) return;
      const pathOwnerId = String(activityDoc?.ref?.parent?.parent?.id || "");
      if (pathOwnerId && pathOwnerId !== actorId) return;
      const key = `${actorId}:${activityDoc.id}`;
      if (!byKey.has(key)) {
        byKey.set(key, {
          id: activityDoc.id,
          actorId,
          data
        });
      }
    });
  });

  return [...byKey.values()].sort(
    (left, right) => toMillis(left.data?.timestamp) - toMillis(right.data?.timestamp)
  );
};
